import { MyShift, TimeSlot } from '../../../domain/myShift';
import { DayShiftSlots } from '../../../infrastructure/api/dailyShiftSlotsApi';
import { DaySlotSelection } from './types';

/**
 * 日別フォームのスロット構築結果
 */
export interface DaySlotBuildResult {
  /** 表示するスロット一覧（開始時刻順） */
  slots: DaySlotSelection[];
  /** 募集枠に一致しない自分のシフト行（「その他の予定」として読み取り専用表示） */
  extraShifts: TimeSlot[];
  /** このフェーズで編集操作（確定→保存）が可能か */
  editable: boolean;
}

const toHm = (time: string): string => time.substring(0, 5);

const keyOf = (start: string, end: string): string => `${start}-${end}`;

const sortByStart = (slots: DaySlotSelection[]): DaySlotSelection[] =>
  [...slots].sort((a, b) => a.startTime.localeCompare(b.startTime));

const lockedRowFrom = (slot: TimeSlot): DaySlotSelection => ({
  key: keyOf(slot.startTime, slot.endTime),
  startTime: slot.startTime,
  endTime: slot.endTime,
  selected: true,
  locked: true,
  source: slot.source,
  slotType: slot.slotType,
});

/**
 * 月次フェーズ・自分のシフト・募集枠から日別フォームのスロットを構築する
 *
 * フェーズ別の挙動（要件書どおり）:
 * - fixed_recruiting: 自分の rule 由来（未確定）シフトが「不可」トグル対象。他は編集不可表示
 * - recruiting: 残枠（shortage）のある募集枠に「希望」トグル。既存応募は選択済み。
 *   残枠数そのものは表示しない（応募が残枠を超過しても可）。
 *   固定確定枠は選択済みロック。募集枠に一致しない自分の行は「その他の予定」
 * - closed: recruiting と同じ構成をすべてロックで表示（選択状態がわかる）
 * - fixed_closed / fixed_confirmed / confirmed: 自分のシフトをロック表示
 * - draft / null: 募集開始前（スロットなし。自分の行があれば「その他の予定」扱い）
 */
export const buildDaySlotSelections = (
  monthlyStatus: string | null,
  ownShift: MyShift | undefined,
  dailySlot: DayShiftSlots | undefined,
): DaySlotBuildResult => {
  const ownSlots = ownShift?.timeSlots ?? [];

  switch (monthlyStatus) {
    case 'fixed_recruiting': {
      const slots = ownSlots.map(slot =>
        slot.source === 'rule' && !slot.isConfirmed
          ? {
              key: keyOf(slot.startTime, slot.endTime),
              startTime: slot.startTime,
              endTime: slot.endTime,
              selected: true,
              locked: false,
              source: slot.source,
              slotType: slot.slotType,
            }
          : lockedRowFrom(slot),
      );

      return { slots: sortByStart(slots), extraShifts: [], editable: true };
    }

    case 'recruiting':
    case 'closed': {
      const recruitSlots = dailySlot?.slots ?? [];
      const ownByKey = new Map(ownSlots.map(s => [keyOf(s.startTime, s.endTime), s]));
      const usedKeys = new Set<string>();

      const slots = recruitSlots.flatMap(ts => {
        const startTime = toHm(ts.startTime);
        const endTime = toHm(ts.endTime);
        const key = keyOf(startTime, endTime);
        const own = ownByKey.get(key);

        // 募集対象は「募集数 − 固定確定数」に残がある枠のみ。
        // shortage_slots は固定フローを通った枠しか再計算されない（インポート直後は0）ため、
        // 保存値ではなくここで計算する。
        // 自分の応募・確定がある枠は残枠ゼロでも表示を維持する（取り下げ・確認のため）
        const remaining = ts.recruitmentSlots - ts.confirmedSlots;
        if (remaining <= 0 && !own) {
          return [];
        }

        if (own) {
          usedKeys.add(key);
        }

        return [
          {
            key,
            startTime,
            endTime,
            selected: own != null,
            locked: monthlyStatus === 'closed' || (own?.isConfirmed ?? false),
            source: own?.source ?? null,
            slotType: own?.slotType ?? null,
          },
        ];
      });

      const extraShifts = ownSlots.filter(
        s => !usedKeys.has(keyOf(s.startTime, s.endTime)),
      );

      return {
        slots: sortByStart(slots),
        extraShifts,
        editable: monthlyStatus === 'recruiting',
      };
    }

    case 'fixed_closed':
    case 'fixed_confirmed':
    case 'confirmed':
      return {
        slots: sortByStart(ownSlots.map(lockedRowFrom)),
        extraShifts: [],
        editable: false,
      };

    default:
      // draft / 月次枠なし: 募集開始前
      return { slots: [], extraShifts: ownSlots, editable: false };
  }
};
